/* eslint-disable @khanacademy/ts-no-error-suppressions */
/**
 * List of discussion posts from users.
 */
import "../../../stylesheets/discussion-package/discussion.less";

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import {connect} from "react-redux";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";

import {faqPageSchema, JsonLd} from "#components/json-ld/json-ld.tsx";
import type {ApolloGqlClient} from "#data-access/gql/apollo-types.ts";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";

import * as sharedStyles from "../shared-styles.ts";
import type {DiscussionContext, FeedbackType, Post} from "../types.ts";
import {EnglishLink, showMainDiscussionInput} from "../util.tsx";

import {
    deselectAll,
    getNextPageAction,
    resetNewPostKeyInfoAction,
    selectErroredPosts,
    selectPosts,
    setFocusOnTopLevelInputAction,
} from "./discussion-actions.ts";
import DiscussionPost from "./discussion-post.tsx";
import type {State} from "./discussion-reducer.ts";

// This type is being exported so that the wrapper component
// StatefulDiscussionList can use it.
type OwnProps = {
    topicSlug: string;
    tab: FeedbackType;
    discussionContext: DiscussionContext;
    clarificationsEnabled?: boolean;
    hasUnansweredHelpRequests?: boolean;
    // Whether this list of posts is being used on a content page.
    onContentPage?: boolean;
    includeCheckboxes?: boolean;
    erroredKeys?: Array<string>;
    onSelectedPostsChanged?: (arg1: State["selectedPosts"]) => void;
    onSelectProgramPost?: (postKey: string) => void;
    selectedProgramKey?: string;
};

type ReduxStateProps = {
    fetchingPosts: boolean;
    newPostKey: string | null | undefined;
    cursor: string;
    isComplete: boolean;
    contentKind: string | null | undefined;
    postKeys: Array<string>;
    posts: Posts;
    selectedPosts: State["selectedPosts"];
};

type ReduxDispatchProps = {
    resetNewPostKeyInfo: () => void;
    onNextPage: (gqlClient: ApolloGqlClient) => void;
    setFocusOnTopLevelInput: () => void;
    onSelectPosts: typeof selectPosts;
    onDeselectAll: typeof deselectAll;
    onSelectErroredPosts: typeof selectErroredPosts;
};

type Posts = Record<string, Post>;

type Props = OwnProps &
    ReduxStateProps &
    ReduxDispatchProps &
    WithUserProfileProps &
    WithApolloProps;

export class DiscussionList extends React.Component<Props> {
    componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<any>,
        snapshot: any,
    ) {
        const {
            selectedPosts,
            onSelectedPostsChanged,
            postKeys,
            onDeselectAll,
            onSelectErroredPosts,
            erroredKeys,
        } = this.props;
        if (!onSelectedPostsChanged) {
            return;
        }

        if (postKeys !== prevProps.postKeys && !erroredKeys) {
            onDeselectAll();
        }
        if (selectedPosts !== prevProps.selectedPosts) {
            onSelectedPostsChanged(selectedPosts);
        }

        if (erroredKeys && erroredKeys !== prevProps.erroredKeys) {
            onSelectErroredPosts(erroredKeys);
        }
    }

    getPostsToShow: () => Array<string> = (): Array<string> => {
        const {postKeys, newPostKey, tab, contentKind, profile} = this.props;

        const isModerator = profile && profile.get("isModerator");
        const hideClarifications =
            tab === "clarifications" &&
            contentKind === "Article" &&
            !isModerator;

        // Clarifications are hidden on articles for non-mods,
        // but we do want to show newly posted clarifications.
        return hideClarifications
            ? postKeys.filter((post) => post === newPostKey)
            : postKeys;
    };

    isCompactView: () => boolean = (): boolean => {
        return this.props.discussionContext.viewMode === "compact";
    };

    isMaximized: () => boolean = (): boolean => {
        return this.props.discussionContext.view === "maximized";
    };

    onClickSelectAll: () => void = () => {
        const {onSelectPosts, postKeys} = this.props;
        onSelectPosts(postKeys);
    };

    maybeRenderListHeader: (postsToShow: Array<string>) => React.ReactNode = (
        postsToShow: Array<string>,
    ) => {
        const {tab, contentKind} = this.props;

        if (
            postsToShow.length ||
            !(contentKind === "Video" && tab === "clarifications")
        ) {
            return null;
        }

        return (
            <LabelLarge style={sharedStyles.clarificationsListHeader}>
                {t`Suggested clarifications`}
            </LabelLarge>
        );
    };

    renderContent: (postsToShow: Array<string>) => React.ReactNode = (
        postsToShow: Array<string>,
    ) => {
        const {
            fetchingPosts,
            newPostKey,
            resetNewPostKeyInfo,
            tab,
            topicSlug,
            discussionContext,
            clarificationsEnabled,
            onContentPage,
            setFocusOnTopLevelInput,
            profileLoading,
            profile,
            contentKind,
            hasUnansweredHelpRequests,
            includeCheckboxes,
            posts,
            onSelectProgramPost,
            selectedProgramKey,
        } = this.props;

        if (!postsToShow.length) {
            if (fetchingPosts) {
                // fetchingPosts is used for fetching the initial posts and subsequent pages,
                // so we only want to show this spinner if we're currently fetching AND
                // we have not yet acquired any posts.
                return (
                    <CircularSpinner
                        size="medium"
                        style={styles.noPostsWrapper}
                    />
                );
            }
            // We only want to show the call to action button when the user can
            // actually post, which is only on content pages and if they have
            // the correct type of account (over 13, enough points, etc.)
            const showCTAButton =
                onContentPage &&
                showMainDiscussionInput(
                    tab,
                    contentKind,
                    discussionContext,
                    profile,
                    hasUnansweredHelpRequests,
                );

            return (
                <View style={styles.noPostsWrapper}>
                    <LabelLarge>{t`No posts yet.`}</LabelLarge>
                    {showCTAButton && (
                        <Button
                            onClick={setFocusOnTopLevelInput}
                            style={styles.noPostsButton}
                        >
                            {t`Join the conversation`}
                        </Button>
                    )}
                </View>
            );
        }

        // We say yes, it's a child account if the profile is loading so child accounts
        // don't initially see interaction buttons that then disappear after the profile
        // has loaded.
        // TODO(diedra): Move all the profile checking to the areas where we actually
        // need to know this information. We should have all the permissions checks in one
        // utils file.
        const isChildAccount =
            profileLoading || (profile && profile.get("isChildAccount"));

        // HACK(chase): includeCheckboxes is used here to determine if the discussion-list
        // is being rendered from the flag-queue
        const onFlagQueue = includeCheckboxes;
        const listStyles = onFlagQueue
            ? [styles.horizontalSeperator]
            : [styles.post, !this.isCompactView() && styles.postExtraSpacing];

        return (
            <React.Fragment>
                <DiscussionListStructuredData tab={tab} posts={posts} />

                <ul>
                    {postsToShow.map((postKey) => {
                        return (
                            <li
                                data-testid="discussion-list-item"
                                key={postKey}
                                className={css(
                                    ...listStyles,
                                    postKey === selectedProgramKey &&
                                        styles.programHighlighted,
                                )}
                            >
                                <div
                                    className={css(
                                        onFlagQueue &&
                                            styles.flagQueuePostWrapper,
                                    )}
                                >
                                    {
                                        <DiscussionPost
                                            tab={tab}
                                            postKey={postKey}
                                            newPostKey={newPostKey}
                                            resetNewPostKeyInfo={
                                                resetNewPostKeyInfo
                                            }
                                            topicSlug={topicSlug}
                                            restrictPosting={
                                                isChildAccount ?? false
                                            }
                                            clarificationsEnabled={
                                                clarificationsEnabled
                                            }
                                            isMaximized={this.isMaximized()}
                                            moderatorMode={
                                                discussionContext.moderatorMode
                                            }
                                            // @ts-expect-error - TS2322 - Type 'ViewMode | undefined' is not assignable to type 'ViewMode'.
                                            viewMode={
                                                discussionContext.viewMode
                                            }
                                            isProjectFeedback={
                                                discussionContext.showProjectFeedback
                                            }
                                            includeCheckbox={includeCheckboxes}
                                            onSelectProgramPost={
                                                onSelectProgramPost
                                            }
                                            selectedProgramKey={
                                                selectedProgramKey
                                            }
                                        />
                                    }
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </React.Fragment>
        );
    };

    renderMultiSelectors: () => React.ReactNode = () => {
        const {selectedPosts} = this.props;
        const total =
            Object.keys(selectedPosts).length > 0
                ? Object.values(selectedPosts).filter((post) => post).length
                : 0;

        return (
            <View style={styles.selectionContainer}>
                <Button
                    kind="secondary"
                    size="small"
                    style={styles.selectAllButton}
                    onClick={this.onClickSelectAll}
                >
                    {t`Select all`}
                </Button>
                <Button
                    kind="secondary"
                    size="small"
                    onClick={this.props.onDeselectAll}
                >
                    {t`Deselect all`}
                </Button>
                {total > 0 && (
                    <div className={css(styles.selectedTotal)}>
                        <>{total} </>
                        <>{t`Selected`} </>
                    </div>
                )}
            </View>
        );
    };

    render(): React.ReactNode {
        const {
            contentKind,
            isComplete,
            cursor,
            onNextPage,
            includeCheckboxes,
            gqlClient,
        } = this.props;

        const postsToShow = this.getPostsToShow();
        const isMaximized = this.isMaximized();

        // HACK(chase): includeCheckboxes is used here to determine if the discussion-list
        // is being rendered from the flag-queue
        const onFlagQueue = includeCheckboxes;

        return (
            <View
                style={[
                    styles.wrapper,
                    isMaximized && styles.maximizedContainer,
                ]}
            >
                {includeCheckboxes && this.renderMultiSelectors()}
                <View
                    style={[
                        !isMaximized && sharedStyles.listWrapper,
                        !this.isCompactView() && styles.listWrapperExtraSpacing,
                    ]}
                >
                    {contentKind === "Topic" && (
                        <EnglishLink style={styles.englishLink} />
                    )}

                    {this.maybeRenderListHeader(postsToShow)}

                    <View style={onFlagQueue && styles.postsWrapper}>
                        {this.renderContent(postsToShow)}
                    </View>
                </View>
                {cursor && !isComplete && (
                    <Button onClick={() => onNextPage(gqlClient)}>
                        {t`Show more comments`}
                    </Button>
                )}
            </View>
        );
    }
}

export const DiscussionListStructuredData: (props: {
    tab: FeedbackType;
    posts: Posts;
}) => React.ReactNode = ({tab, posts}) => {
    if (tab !== "questions") {
        return null;
    }

    // We want the visible-on-render questions (top 10) that have answers,
    // not the top 10 questions with answers.
    const answeredQAs = Object.keys(posts)
        .reduce<Array<any>>((acc: any, postKey: string) => {
            const question = posts[postKey];
            if (!question.answers?.length) {
                return acc;
            }
            const firstAnswer = posts[question.answers[0]];
            if (
                question &&
                question.feedbackType === "QUESTION" &&
                firstAnswer &&
                firstAnswer.content &&
                firstAnswer.sumVotesIncremented > 1 // quality
            ) {
                acc.push({q: question, a: firstAnswer});
            }
            return acc;
        }, [])
        .sort((a, b) => b.q.sumVotesIncremented - a.q.sumVotesIncremented);

    return (
        !!answeredQAs.length && (
            <JsonLd
                schema={faqPageSchema({
                    mainEntity: answeredQAs.map(({q, a}) => ({
                        name: q.content,
                        acceptedAnswer: {
                            text: a.content,
                        },
                    })),
                })}
            />
        )
    );
};

const styles = StyleSheet.create({
    wrapper: {
        width: "100%",
    },
    // The maximized view is used on the moderator flag queue found at
    // /devadmin/discussion/mod/flaggedfeedback
    maximizedContainer: {
        backgroundColor: color.white,
        maxWidth: "100%",
        padding: "0 20px",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    listWrapper: {
        backgroundColor: color.white,
        paddingTop: spacing.medium_16,
    },
    listWrapperExtraSpacing: {
        paddingTop: spacing.xLarge_32,
    },
    post: {
        ...sharedStyles.postWrapper,
    },
    postExtraSpacing: {
        marginBottom: spacing.large_24,
    },
    flagQueuePostWrapper: {
        padding: spacing.small_12,
    },
    horizontalSeperator: {
        borderBottom: sharedStyles.border,
        clear: "both",
        paddingBottom: spacing.small_12,
        ":last-child": {
            borderBottom: "none",
            paddingBottom: 0,
        },
    },
    programHighlighted: {
        background: color.fadedBlue8,
        boxShadow: `inset 4px 0 ${color.blue}`,
        ":first-child": {
            borderTopRightRadius: "12px",
            borderTopLeftRadius: "12px",
        },
        ":last-child": {
            borderBottomRightRadius: "12px",
            borderBottomLeftRadius: "12px",
        },
    },
    // eslint-disable-next-line react-native/no-unused-styles
    linkStyledButton: {
        color: color.offBlack64,
        border: "none",
        backgroundColor: "transparent",
        cursor: "pointer",
        padding: 0,
        margin: 0,
    },
    englishLink: {
        marginBottom: 20,
    },
    postsWrapper: {
        borderWidth: "1px",
        borderRadius: "12px",
        borderColor: color.offBlack16,
    },
    noPostsWrapper: {
        textAlign: "center",
        margin: "0 auto",
        padding: `${spacing.xLarge_32}px 0 ${spacing.medium_16}px`,
    },
    noPostsButton: {
        marginTop: spacing.medium_16,
    },
    selectionContainer: {
        flexDirection: "row",
        marginTop: spacing.medium_16,
    },
    selectAllButton: {
        marginRight: 4,
    },
    selectedTotal: {
        marginLeft: 10,
        paddingTop: 5,
    },
});

const mapStateToProps = (state: State) => {
    const tab = state.tabs[state.currentTab];

    return {
        fetchingPosts: state.fetchingPostsStatus === "loading",
        tab: state.currentTab,
        newPostKey: state.newPostKey,
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        cursor: tab.cursor,
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        isComplete: tab.isComplete,
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        postKeys: tab.postKeys,
        contentKind: state.contentKind,
        selectedPosts: state.selectedPosts,
        posts: state.posts,
    };
};

const mapDispatchToProps = {
    resetNewPostKeyInfo: resetNewPostKeyInfoAction,
    onNextPage: getNextPageAction,
    setFocusOnTopLevelInput: setFocusOnTopLevelInputAction,
    onSelectPosts: selectPosts,
    onDeselectAll: deselectAll,
    onSelectErroredPosts: selectErroredPosts,
} as const;

type ExportProps = WithoutApollo<
    WithoutUserProfile<
        Without<
            Without<
                JSX.LibraryManagedAttributes<
                    typeof DiscussionList,
                    React.ComponentProps<typeof DiscussionList>
                >,
                ReduxStateProps
            >,
            ReduxDispatchProps
        >
    >
>;

export default compose(
    withApollo,
    withUserProfile,
    connect(mapStateToProps, mapDispatchToProps),
)(DiscussionList) as React.ComponentType<ExportProps>;
